import { filter, forEach, groupBy } from 'lodash/collection';
import { ApplicationState } from '.';
import moment from 'moment';
import { DebtorClaimStatus, DebtorClaim, AvailablePowerOfAttorney, DebtorLogItem, LeapClaim, SelectedDebtorClaim, CreditType, DebtorClaimStage, DebtorClaimEvent } from '../api/client';
import { StepsGroup, steps } from '../components/conversationSupervisor/components/ConversationSupervisorStep';
import { DebtorClaimsState } from './DebtorClaimsStore';
import { DebtorLeapClaimsState } from './DebtorLeapClaimsStore';
import { ConversationSupervisorState } from './ConversationSupervisorStore';

export const getTotalOutstanding = (state: ApplicationState) => {
  return state.debtorLeapClaims.claims
    .filter(x =>
      x.isTopLevelClaim
      && x.amount
      && ((x.type === 'LeapClaim' && !x.debtCollectionExternalReferenceNumber) || x.type === 'LeapDebtCollectionClaim'))
    .reduce((a, b) => +a + +b.amount!, 0);
};

export const getOutstandingDebtorClaims = (debtorLeapClaims: DebtorLeapClaimsState) => {
  let claims = debtorLeapClaims.claims.filter(x => x.isTopLevelClaim && x.amount && x.amount > 0);
  return claims;
};

export const getDebtorClaimsForObjectionRegistration = (debtorLeapClaims: DebtorLeapClaimsState) => {
  return debtorLeapClaims.claims
    .filter(x => x.canRegisterObjection);
};

export const getDebtorClaimsEligibleForCredit = (debtorLeapClaims: DebtorLeapClaimsState) => {
  return debtorLeapClaims.claims
    .filter(x => x.canRegisterCreditNote);
};

export const getDebtorClaimsEligibleForFeeCredit = (debtorLeapClaims: DebtorLeapClaimsState) => {
  return debtorLeapClaims.claims
    .filter(x => x.canRegisterCreditNote && x.hasFee);
};

export const getDebtorClaimsEligibleForInvoiceFeeCredit = (debtorLeapClaims: DebtorLeapClaimsState) => {
  return debtorLeapClaims.claims
    .filter(x => x.canRegisterCreditNote && x.hasInvoiceFee);
};

export const getDebtorClaimsEligibleForNoticeFeeCredit = (debtorLeapClaims: DebtorLeapClaimsState) => {
  return debtorLeapClaims.claims
    .filter(x => x.canRegisterCreditNote && x.hasNoticeFee);
};

export const hasSelectedDebtorClaimsEligibleForFeeCredit = (state: ApplicationState) => {
  let result = false;
  state.debtorClaims.selectedDebtorClaims.forEach((selectedDebtorClaim) => {
    const isEligible: boolean = getDebtorClaimsEligibleForFeeCredit(state.debtorLeapClaims).some((eligibleDebtorClaim) =>
      eligibleDebtorClaim.number === selectedDebtorClaim.number
      && eligibleDebtorClaim.providerSystem === selectedDebtorClaim.providerSystem
      && eligibleDebtorClaim.externalReferenceId === selectedDebtorClaim.externalReferenceId);    
    if (isEligible) { result = true; }
  });
  return result;
}

export const hasSelectedDebtorClaimsEligibleForInvoiceFeeCredit = (state: ApplicationState) => {
  let result = false;
  state.debtorClaims.selectedDebtorClaims.forEach((selectedDebtorClaim) => {
    const isEligible: boolean = getDebtorClaimsEligibleForInvoiceFeeCredit(state.debtorLeapClaims).some((eligibleDebtorClaim) =>
      eligibleDebtorClaim.number === selectedDebtorClaim.number
      && eligibleDebtorClaim.providerSystem === selectedDebtorClaim.providerSystem
      && eligibleDebtorClaim.externalReferenceId === selectedDebtorClaim.externalReferenceId);
    if (isEligible) { result = true; }
  });
  return result;
}

export const hasSelectedDebtorClaimsEligibleForNoticeFeeCredit = (state: ApplicationState) => {
  let result = false;
  state.debtorClaims.selectedDebtorClaims.forEach((selectedDebtorClaim) => {
    const isEligible: boolean = getDebtorClaimsEligibleForNoticeFeeCredit(state.debtorLeapClaims).some((eligibleDebtorClaim) =>
      eligibleDebtorClaim.number === selectedDebtorClaim.number
      && eligibleDebtorClaim.providerSystem === selectedDebtorClaim.providerSystem
      && eligibleDebtorClaim.externalReferenceId === selectedDebtorClaim.externalReferenceId);
    if (isEligible) { result = true; }
  });
  return result;
}
export const getValidSelectedDebtorClaimsForCredit = (selectedDebtorClaims: SelectedDebtorClaim[], creditType: CreditType) => {
  if (creditType === CreditType.Full) {
    return selectedDebtorClaims.filter(x => x.balanceAmount && x.balanceAmount > 0);
  }

  if (creditType === CreditType.Fees) {
    return selectedDebtorClaims.filter(x => (x.invoiceFeeAmount && x.invoiceFeeAmount > 0) || (x.noticeFeeAmount && x.noticeFeeAmount > 0));
  }

  if (creditType === CreditType.InvoiceFee) {
    return selectedDebtorClaims.filter(x => x.invoiceFeeAmount && x.invoiceFeeAmount > 0);
  }

  if (creditType === CreditType.DebtCollectionNoticeFee) {
    return selectedDebtorClaims.filter(x => x.noticeFeeAmount && x.noticeFeeAmount > 0);
  }

  return [];
}

export const getDebtorLogItems = (state: ApplicationState) => {

  const logItems = Array<DebtorLogItem>();

  const dateTime = item => moment(item.registeredAt).format('YYYY-MM-DD HH');
  const groupedLogItemsByRegisteredAt = groupBy(state.debtorLogItems.logItems, dateTime);
  forEach(groupedLogItemsByRegisteredAt, groupByRegisteredAt => {
    const groupedLogItemsByRegisteredBy = groupBy(groupByRegisteredAt, 'registeredBy');
    forEach(groupedLogItemsByRegisteredBy, groupByRegisteredBy => {
      const groupedLogItemsByText = groupBy(groupByRegisteredBy, 'text');
      forEach(groupedLogItemsByText, groupByText => {

        let currentLogItem;

        forEach(groupByText.valueOf() as Array<DebtorLogItem>, logItem => {

          if (!currentLogItem || !currentLogItem.debtorClaimNumbers) {
            currentLogItem = logItem;
          } else {
            forEach(logItem.debtorClaimNumbers, claimNumber => {
              if (!currentLogItem.debtorClaimNumbers.includes(claimNumber)) {
                currentLogItem.debtorClaimNumbers.push(claimNumber);
              }
            });
          }
        });

        if (currentLogItem) {
          logItems.push(currentLogItem);
        }

      });
    });
  });

  const selectedDebtorClaims = state.debtorClaims.selectedDebtorClaims;

  return logItems.sort((a, b): number => { if (a.registeredAt && b.registeredAt && a.registeredAt <= b.registeredAt) { return 1; } else { return -1; } })
    .sort((a, b): number => {
      return (a.debtorClaimNumbers && a.debtorClaimNumbers!.filter(o => selectedDebtorClaims.filter(n => n.number === o).length > 0).length > 0) ===
      (b.debtorClaimNumbers && b.debtorClaimNumbers!.filter(o => selectedDebtorClaims.filter(n => n.number === o).length > 0).length > 0) ? 0 :
      a.debtorClaimNumbers && a.debtorClaimNumbers!.filter(o => selectedDebtorClaims.filter(n => n.number === o).length > 0).length > 0 ? -1 : 1;
      }
  );
};

export const getConversationActions = (state: ApplicationState, debtorClaim: DebtorClaim) => {
  let actionCount = 0;
  filter(state.conversationSupervisor.conversation.actions, (element) => {
    actionCount += filter(element.selectedDebtorClaims, { number: debtorClaim.number, type: debtorClaim.type }).length;
  });

  return actionCount;
};

export const getSubmittedConversations = (state: ApplicationState) => {
  return state.conversationSupervisor.submittedConversations
    .filter(conversations => { return moment(conversations.submittedAt) >= moment().startOf('day'); })
    .sort((a, b): number => { return a.submittedAt <= b.submittedAt ? 1 : -1; });
};

const getSortingPriorityFromStatus = (status: DebtorClaimStatus, claimStage: DebtorClaimStage | undefined) => {

  var isInvoice = claimStage === DebtorClaimStage.Invoice;
  switch (status) {
    case DebtorClaimStatus.Active:
      return isInvoice ? 100 :  80;
    case DebtorClaimStatus.ExternalDebtCollection:
      return 70;
    case DebtorClaimStatus.Halted:
    case DebtorClaimStatus.ObjectionReceived:
      return 60;
    case DebtorClaimStatus.Credited:
    case DebtorClaimStatus.Forfeited:
      return 50;
    case DebtorClaimStatus.Paid:
      return 10;
    case DebtorClaimStatus.Expired:
      return 0;
    default:
      return -1;
  }
};

function sortDebtorClaims<T extends DebtorClaim>(a: T, b: T) {
  if (!a?.date || !b?.date) return 0;
  if (!a.status || !b.status) return 0;

  const sortingPriorityA = getSortingPriorityFromStatus(a.status, a.claimStage);
  const sortingPriorityB = getSortingPriorityFromStatus(b.status, b.claimStage);

  if (sortingPriorityA !== sortingPriorityB) {
    return sortingPriorityA > sortingPriorityB ? -1 : 1;
  }

  if (a.date < b.date) return 1;
  if (a.date > b.date) return -1;
  return 0;
  
};

export const getDebtorClaims = (state: ApplicationState) => {
  return state.debtorLeapClaims.claims
    .filter((claim) =>
      ((claim.type === 'LeapClaim' && claim.debtCollectionExternalReferenceNumber == null) || claim.type === 'LeapDebtCollectionClaim'))
    .sort(sortDebtorClaims);
};

const filterAwayClaimsWithoutNumberOrKid = (invoice: LeapClaim) => !!invoice.kid && !!invoice.number;

export const getActiveLeapClaimsForDebtOverview = (state: ApplicationState) => {
  return state.debtorLeapClaims.claims ? state.debtorLeapClaims.claims
    .filter(x => ((x.type === 'LeapClaim' && x.debtCollectionExternalReferenceNumber == null) || x.type === 'LeapDebtCollectionClaim')
            && filterAwayClaimsWithoutNumberOrKid(x)
            && x.isTopLevelClaim
            && (x.status === DebtorClaimStatus.Active || x.status === DebtorClaimStatus.Halted))
    .sort(sortDebtorClaims) : [];
};

export const getActiveLeapClaims = (state: ApplicationState) => {
  return state.debtorLeapClaims.claims ? state.debtorLeapClaims.claims
    .filter(x => ((x.type === 'LeapClaim' && x.debtCollectionExternalReferenceNumber == null) || x.type === 'LeapDebtCollectionClaim')
      && x.isTopLevelClaim
      && (x.status === DebtorClaimStatus.Active || x.status === DebtorClaimStatus.Halted))
    .sort(sortDebtorClaims) : [];
};

export const isDebtorClaimSelectableForConversationSupervisorStep = (conversationSupervisor: ConversationSupervisorState, debtorClaim: DebtorClaim) => {
  const currentStep = steps.find(x => x.id === conversationSupervisor.step);
  if (currentStep === undefined) {
    return true;
  }

  switch (currentStep.group) {
    case StepsGroup.WillPay:
      return debtorClaim.canSendPaymentInformation;
    case StepsGroup.HasObjection:
      return debtorClaim.canRegisterObjection;
    case StepsGroup.Credit:
      return debtorClaim.canRegisterCreditNote;
    case StepsGroup.Postpone:
      return debtorClaim.canPostpone
    default:
      return true;
  }
};

export const hasSelectedDebtorClaims = (state: DebtorClaimsState) => {
  return state.selectedDebtorClaims.length > 0;
};

export const getTopLevelLeapClaimsFromSelectedClaims = (state: DebtorClaimsState, leapClaimState: DebtorLeapClaimsState) => {
  let leapClaims = leapClaimState.claims.filter(x => x.isTopLevelClaim && state.selectedDebtorClaims.filter(n => n.externalReferenceId === x.externalReferenceId && n.number === x.number).length > 0);
  return leapClaims;
};

export const getAllLeapClaimsFromSelectedClaims = (state: DebtorClaimsState, leapClaimState: DebtorLeapClaimsState) => {
  const selectedKeySet = new Set(state.selectedDebtorClaims.map(claim => `${claim.externalReferenceId}::${claim.number}`));
  return findSelectedClaims(leapClaimState.claims, selectedKeySet);
};

function findSelectedClaims(claims: LeapClaim[], selectedKeys: Set<string>): LeapClaim[] {

  const result: LeapClaim[] = [];

  for (const claim of claims) {
    const key = `${claim.externalReferenceId}::${claim.number}`;

    if (selectedKeys.has(key)) {
      result.push(claim);
    }

    if (claim.childClaims?.length) {
      result.push(...findSelectedClaims(claim.childClaims, selectedKeys));
    }
  }

  return result;
}

export const getSelectedDebtorClaims = (state: DebtorClaimsState) => {
  return state.selectedDebtorClaims;
};

const sortPowerOfAttorneys = (a: AvailablePowerOfAttorney, b: AvailablePowerOfAttorney) => {
  return (a.active === b.active) ? a.registeredAtUtc && b.registeredAtUtc && (a.registeredAtUtc > b.registeredAtUtc) ? -1 : 1 : a.active ? -1 : 1;
};

export const getExistingPowerOfAttorneys = (state: ApplicationState, debtorId: string) => {
  return state.powerOfAttorney.powerOfAttorneyByIds && state.powerOfAttorney.powerOfAttorneyByIds[debtorId].sort(sortPowerOfAttorneys);
};

export const getActivePowerOfAttorneysCount = (state: ApplicationState, debtorId: string): number => {
  const powerOfAttorneys = state.powerOfAttorney.powerOfAttorneyByIds && state.powerOfAttorney.powerOfAttorneyByIds[debtorId];
  if (!powerOfAttorneys) {
    return 0;
  }

  return powerOfAttorneys.filter(item => item.active).length; 
};

export const getPowerOfAttorneysByDebtorId = (state: ApplicationState, debtorId: string): AvailablePowerOfAttorney[] => {
  return state.powerOfAttorney.powerOfAttorneyByIds !== undefined ? state.powerOfAttorney.powerOfAttorneyByIds[debtorId] : [];
}

export const getDebtorClaimsEligibleForPostpone = (state: ApplicationState) => {
  return state.debtorLeapClaims.claims
    .filter(x => x.canPostpone);
};

export const getSelectedDebtorClaimsWithChildClaims = (leapDebtorClaims: LeapClaim[]) => {
  if (!leapDebtorClaims) {
    return [];
  }

  const result: SelectedDebtorClaim[] = [];

  for (const claim of leapDebtorClaims) {
    if (claim.number) {
      result.push({ type: claim.type, number: claim.number, externalReferenceId: claim.externalReferenceId, providerSystem: claim.providerSystem });
    }

    if (claim.type === "LeapDebtCollectionClaim" && claim.childClaims && claim.childClaims.length > 0) {
      result.push(...claim.childClaims.filter(x => x.type === "LeapDebtCollectionClaim").map(x => ({ type: x.type, number: x.number, externalReferenceId: x.externalReferenceId, providerSystem: x.providerSystem })));
    }
  }

  return result;
}

export const getEligibleActiveLeapClaimsForCurrentStep = (state: ApplicationState) => {
  return getActiveLeapClaims(state).filter(
    claim => isDebtorClaimSelectableForConversationSupervisorStep(state.conversationSupervisor, claim)
  );
};